package puzzlekey

import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Promise
import kotlin.js.json

actual object Crypto : Aead, Kdf {
    actual override suspend fun encrypt(key: ByteArray, iv: ByteArray, plaintext: ByteArray): ByteArray {
        val aesKey = subtle.importKey("raw", key, "AES-GCM", false, arrayOf("encrypt")).await()
        return subtle.encrypt(json("name" to "AES-GCM", "iv" to iv, "tagLength" to 128), aesKey, plaintext).await().toByteArray()
    }

    actual override suspend fun decrypt(key: ByteArray, iv: ByteArray, ciphertext: ByteArray): ByteArray {
        val aesKey = subtle.importKey("raw", key, "AES-GCM", false, arrayOf("decrypt")).await()
        val plaintext = try {
            subtle.decrypt(json("name" to "AES-GCM", "iv" to iv, "tagLength" to 128), aesKey, ciphertext).await()
        } catch (e: Throwable) {
            throw AeadException(e) // WebCrypto rejects a failed tag with a bare OperationError
        }
        return plaintext.toByteArray()
    }

    actual override suspend fun hkdf(ikm: ByteArray, salt: ByteArray, info: ByteArray, length: Int): ByteArray {
        val key = subtle.importKey("raw", ikm, "HKDF", false, arrayOf("deriveBits")).await()
        val params = json("name" to "HKDF", "hash" to "SHA-256", "salt" to salt, "info" to info)
        return subtle.deriveBits(params, key, length * 8).await().toByteArray()
    }

    actual override suspend fun pbkdf2(password: ByteArray, salt: ByteArray, iterations: Int, length: Int): ByteArray {
        val key = subtle.importKey("raw", password, "PBKDF2", false, arrayOf("deriveBits")).await()
        val params = json("name" to "PBKDF2", "hash" to "SHA-256", "salt" to salt, "iterations" to iterations)
        return subtle.deriveBits(params, key, length * 8).await().toByteArray()
    }
}

private external interface CryptoKey

private external interface ArrayBuffer

private external interface SubtleCrypto {
    fun importKey(
        format: String, keyData: ByteArray, algorithm: Any, extractable: Boolean, keyUsages: Array<String>,
    ): Promise<CryptoKey>

    fun deriveBits(algorithm: Any, baseKey: CryptoKey, length: Int): Promise<ArrayBuffer>

    fun encrypt(algorithm: Any, key: CryptoKey, data: ByteArray): Promise<ArrayBuffer>

    fun decrypt(algorithm: Any, key: CryptoKey, data: ByteArray): Promise<ArrayBuffer>
}

private val subtle: SubtleCrypto = js("globalThis.crypto.subtle")

private fun ArrayBuffer.toByteArray(): ByteArray = int8Array(this)

private fun int8Array(buffer: ArrayBuffer): ByteArray = js("new Int8Array(buffer)")

internal suspend fun <T> Promise<T>.await(): T = suspendCoroutine { cont ->
    then({ cont.resume(it) }, { cont.resumeWithException(it) })
}
